/**
 * Alignment line renderer
 * Provides methods for drawing alignment lines on canvas
 */

import { px2cm } from '~src/utils/units/px2cm';
import { getCurrentDPI } from '../../ruler/utils';

/**
 * Draw a vertical alignment line
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {Object} line - Line configuration
 * @param {Array} viewportTransform - Canvas viewport transform
 * @param {number} zoom - Canvas zoom level
 */
export function drawVerticalAlignmentLine(ctx, line, viewportTransform, zoom) {
  const x = Math.round((line.x + viewportTransform[4]) * zoom);
  const y1 = Math.round((line.y1 + viewportTransform[5]) * zoom);
  const y2 = Math.round((line.y2 + viewportTransform[5]) * zoom);

  // Apply dotted style for distance lines
  if (line.distance !== undefined) {
    ctx.setLineDash([5, 5]);
  }

  ctx.beginPath();
  ctx.moveTo(x + 0.5, y1);
  ctx.lineTo(x + 0.5, y2);
  ctx.stroke();
}

/**
 * Draw a horizontal alignment line
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {Object} line - Line configuration
 * @param {Array} viewportTransform - Canvas viewport transform
 * @param {number} zoom - Canvas zoom level
 */
export function drawHorizontalAlignmentLine(
  ctx,
  line,
  viewportTransform,
  zoom
) {
  const y = Math.round((line.y + viewportTransform[5]) * zoom);
  const x1 = Math.round((line.x1 + viewportTransform[4]) * zoom);
  const x2 = Math.round((line.x2 + viewportTransform[4]) * zoom);

  // Apply dotted style for distance lines
  if (line.distance !== undefined) {
    ctx.setLineDash([5, 5]);
  }

  ctx.beginPath();
  ctx.moveTo(x1, y + 0.5);
  ctx.lineTo(x2, y + 0.5);
  ctx.stroke();
}

/**
 * Draw an alignment line (delegates to specific type)
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {Object} line - Line configuration with type, coordinates, color, width
 * @param {Array} viewportTransform - Canvas viewport transform
 * @param {number} zoom - Canvas zoom level
 * @param {Object} options - Additional rendering options
 */
export function drawAlignmentLine(
  ctx,
  line,
  viewportTransform,
  zoom,
  options = {}
) {
  ctx.save();

  try {
    // Set line properties - use options which should contain editorConfig values
    ctx.lineWidth =
      line.width || options.lineWidth || options.aligningLineWidth || 1;
    ctx.strokeStyle =
      line.color || options.lineColor || options.objectAlignmentLineColor;

    // Clear shadow effects
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // Add dashed line for non-snapped alignments or distance lines
    if (
      (!line.isSnapped && options.useDashedLines) ||
      line.distance !== undefined
    ) {
      ctx.setLineDash([5, 5]);
    }

    // Draw based on type
    if (line.type === 'vertical') {
      drawVerticalAlignmentLine(ctx, line, viewportTransform, zoom);
    } else if (line.type === 'horizontal') {
      drawHorizontalAlignmentLine(ctx, line, viewportTransform, zoom);
    }
  } finally {
    ctx.restore();
  }
}

/**
 * Draw distance text on alignment line
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {Object} line - Line configuration with distance
 * @param {Array} viewportTransform - Canvas viewport transform
 * @param {number} zoom - Canvas zoom level
 * @param {Object} options - Additional rendering options
 */
export function drawDistanceText(
  ctx,
  line,
  viewportTransform,
  zoom,
  options = {}
) {
  if (!line.distance && line.distance !== 0) return;

  const dpi = getCurrentDPI();
  const distanceInCm = px2cm(line.distance, dpi);
  const distanceText = `${distanceInCm.toFixed(1)} cm`;

  // Set text style
  ctx.save();
  const fontSize = Math.max(14, 14 * zoom);
  ctx.font = `${fontSize}px Arial`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  let textX, textY;

  if (line.type === 'vertical') {
    // For vertical lines, place text horizontally in the middle
    textX = Math.round((line.x + viewportTransform[4]) * zoom);
    textY = Math.round(((line.y1 + line.y2) / 2 + viewportTransform[5]) * zoom);
  } else if (line.type === 'horizontal') {
    // For horizontal lines, place text vertically in the middle
    textX = Math.round(((line.x1 + line.x2) / 2 + viewportTransform[4]) * zoom);
    textY = Math.round((line.y + viewportTransform[5]) * zoom);
  }

  // Measure text for background
  const textMetrics = ctx.measureText(distanceText);
  const padding = 4;
  const bgWidth = textMetrics.width + padding * 2;
  const bgHeight = fontSize + padding;

  // Get canvas dimensions
  const canvasWidth = ctx.canvas.width;
  const canvasHeight = ctx.canvas.height;

  // Apply boundary checks to keep text box inside canvas
  const halfWidth = bgWidth / 2;
  const halfHeight = bgHeight / 2;
  const margin = 5; // Extra margin from canvas edge

  // Adjust X position if text box would go outside canvas bounds
  if (textX - halfWidth < margin) {
    textX = halfWidth + margin;
  } else if (textX + halfWidth > canvasWidth - margin) {
    textX = canvasWidth - halfWidth - margin;
  }

  // Adjust Y position if text box would go outside canvas bounds
  if (textY - halfHeight < margin) {
    textY = halfHeight + margin;
  } else if (textY + halfHeight > canvasHeight - margin) {
    textY = canvasHeight - halfHeight - margin;
  }

  // Draw background rectangle
  ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
  ctx.fillRect(textX - bgWidth / 2, textY - bgHeight / 2, bgWidth, bgHeight);

  // Draw border
  ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
  ctx.lineWidth = 1;
  ctx.strokeRect(textX - bgWidth / 2, textY - bgHeight / 2, bgWidth, bgHeight);

  // Draw the text
  ctx.fillStyle = options.distanceTextColor || '#333333';
  ctx.fillText(distanceText, textX, textY);

  ctx.restore();
}

/**
 * Batch draw multiple alignment lines
 * @param {CanvasRenderingContext2D} ctx - The canvas context
 * @param {Array} lines - Array of line configurations
 * @param {Array} viewportTransform - Canvas viewport transform
 * @param {number} zoom - Canvas zoom level
 * @param {Object} options - Additional rendering options
 */
export function drawAlignmentLines(
  ctx,
  lines,
  viewportTransform,
  zoom,
  options = {}
) {
  if (!lines || lines.length === 0) return;

  // Group lines by style for efficient rendering
  const lineGroups = new Map();

  for (const line of lines) {
    const key = `${line.color || options.lineColor}-${
      line.width || options.lineWidth
    }-${line.isSnapped}`;
    if (!lineGroups.has(key)) {
      lineGroups.set(key, []);
    }
    lineGroups.get(key).push(line);
  }

  // Draw each group with shared style
  for (const [key, groupLines] of lineGroups) {
    const [color, width, isSnapped] = key.split('-');

    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = parseFloat(width);

    // Clear shadow effects
    ctx.shadowColor = 'transparent';
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;

    // Add dashed line for non-snapped alignments
    if (isSnapped === 'false' && options.useDashedLines) {
      ctx.setLineDash([5, 5]);
    }

    for (const line of groupLines) {
      if (line.type === 'vertical') {
        drawVerticalAlignmentLine(ctx, line, viewportTransform, zoom);
      } else if (line.type === 'horizontal') {
        drawHorizontalAlignmentLine(ctx, line, viewportTransform, zoom);
      }
    }

    ctx.restore();
  }

  // Draw distance texts after lines so they appear on top
  for (const line of lines) {
    drawDistanceText(ctx, line, viewportTransform, zoom, options);
  }
}
